/**
 * Track all initialized globals so that we can uninitialize them between tests to avoid cross-test
 * interference.
 */
let resetFuncs: Array<() => void> = [];

export function resetLazyGlobalsForTests(): void {
  const resetFuncsToCall = resetFuncs;
  resetFuncs = [];
  for (const reset of resetFuncsToCall) {
    reset();
  }
}

/**
 * Helper to lazy-initialize a value at the top level and schedule it to be re-initialized between tests.
 *
 * This has a few benefits:
 * - Any top-level work depending on other modules might lead to circular import issues, which this avoids.
 * - Any top-level expensive work can slow down the application import process.
 * - Any top-level state should be reset between tests, which this does automatically.
 *
 * Example usage:
 * export const getFoo = lazyInit<Foo>(() => new Foo());
 */
export default function lazyInit<T>(initFn: () => T): () => T {
  let value: T | null = null;
  let isInitialized = false;
  return () => {
    if (!isInitialized) {
      value = initFn();
      isInitialized = true;
      resetFuncs.push(() => {
        // Drop the reference to avoid possible GC issues.
        value = null;
        isInitialized = false;
      });
    }
    return value!;
  };
}
